import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Heart, Star, MoreVertical, Search } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function MyListingsPage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    try {
      const data = await api.getMyListings();
      setListings(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.deleteListing(deleteId);
      setListings(listings.filter(l => l.id !== deleteId));
      toast.success('Anuncio eliminado');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const handleFeature = async (id) => {
    try {
      const result = await api.featureListing(id, window.location.origin);
      window.location.href = result.url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filteredListings = listings.filter(l => 
    l.title.toLowerCase().includes(search.toLowerCase())
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  return (
    <div data-testid="my-listings-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 font-[Outfit]">
            Mis anuncios
          </h1>
          <p className="text-zinc-500">{listings.length} anuncios en total</p>
        </div>
        <Link to="/dashboard/anuncios/nuevo">
          <Button className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="create-listing-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo anuncio
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <Input
          type="text"
          placeholder="Buscar anuncios..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-12 bg-white/5 border-white/10 text-zinc-100"
        />
      </div>

      {/* Listings Table */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-lg" />
            ))}
          </div>
        ) : filteredListings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider px-6 py-4">
                    Anuncio
                  </th>
                  <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider px-6 py-4 hidden md:table-cell">
                    Precio
                  </th>
                  <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider px-6 py-4 hidden md:table-cell">
                    Estado
                  </th>
                  <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider px-6 py-4 hidden lg:table-cell">
                    Vistas
                  </th>
                  <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider px-6 py-4 hidden lg:table-cell">
                    Favoritos
                  </th>
                  <th className="text-right text-xs font-medium text-zinc-500 uppercase tracking-wider px-6 py-4">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredListings.map((listing) => (
                  <tr key={listing.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                          {listing.images?.[0] && (
                            <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <Link 
                            to={`/anuncio/${listing.id}`}
                            className="font-medium text-zinc-100 hover:text-white truncate block"
                          >
                            {listing.title}
                          </Link>
                          <p className="text-sm text-zinc-500">{listing.category_name}</p>
                          <p className="text-sm text-zinc-100 md:hidden">{formatPrice(listing.price)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="font-medium text-zinc-100">{formatPrice(listing.price)}</span>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        listing.status === 'active' 
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : listing.status === 'sold'
                            ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                      }`}>
                        {listing.status === 'active' ? 'Activo' : listing.status === 'sold' ? 'Vendido' : 'Pausado'}
                      </span>
                      {listing.is_featured && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20">
                          <Star className="w-3 h-3 mr-1" />
                          Destacado
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="flex items-center gap-1 text-zinc-400">
                        <Eye className="w-4 h-4" />
                        {listing.views || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="flex items-center gap-1 text-zinc-400">
                        <Heart className="w-4 h-4" />
                        {listing.favorites_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-zinc-400">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#121215] border-white/10">
                          <DropdownMenuItem asChild>
                            <Link to={`/anuncio/${listing.id}`} className="flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              Ver
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/dashboard/anuncios/${listing.id}/editar`} className="flex items-center gap-2">
                              <Edit className="w-4 h-4" />
                              Editar
                            </Link>
                          </DropdownMenuItem>
                          {!listing.is_featured && (
                            <DropdownMenuItem onClick={() => handleFeature(listing.id)} className="flex items-center gap-2">
                              <Star className="w-4 h-4" />
                              Destacar
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => setDeleteId(listing.id)}
                            className="flex items-center gap-2 text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-zinc-500 mb-4">No tienes anuncios aún</p>
            <Link to="/dashboard/anuncios/nuevo">
              <Button className="bg-zinc-100 text-zinc-900 hover:bg-white">
                <Plus className="w-4 h-4 mr-2" />
                Crear primer anuncio
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#121215] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">¿Eliminar anuncio?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción no se puede deshacer. El anuncio será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-zinc-300">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
